/**
 * DANGER ZONE — everything that stops a till from selling, kept apart.
 *
 * Nothing here is decided again: Clear pairing and Factory wipe open the SAME
 * `DestructiveConfirm` the support drawer uses (live impact probe → typed
 * register name → reason → emailed single-use code → "Arm on next contact"
 * when the till is offline). Remove from fleet is the same `deleteDevice`
 * call, with the same honest dialog, the legacy Fleet table uses. Reinstall
 * OS can never run remotely; it goes to the bench flow on the Install tab and
 * is only offered when there is a unit to put on the bench.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2 } from 'lucide-react';
import type { FleetActionId, FleetCommand } from '@platform/shared-types';
import { useDeviceCommands } from '../../pos/fleet/useDeviceCommands';
import DestructiveConfirm from '../../pos/fleet/DestructiveConfirm';
import { humaniseCommandError } from '../../pos/fleet/FleetDeviceDrawer';
import type { FleetRow } from '../../pos/fleet/fleetRows';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { toastService } from '../../../services/toastService';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { DEFAULT_DEVICE_TYPE } from '../../pos/device/useFleetDevice';
import type { DeviceTabProps } from '../../pos/device/deviceDetail';
import { useCanManageFleet } from './useCanManageFleet';

/** "Checked in minutes ago" — the same 10-minute window the Fleet table uses. */
const LIVE_WITHIN_MS = 10 * 60 * 1000;

function DangerRow({
  title, detail, button, onClick, disabled, disabledReason,
}: {
  title: string; detail: string; button: string; onClick: () => void; disabled?: boolean; disabledReason?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-stone-200 px-4 py-3.5 last:border-b-0">
      <div className="min-w-0 max-w-[640px]">
        <div className="font-semibold text-stone-900">{title}</div>
        <div className="mt-0.5 text-[13px] text-stone-500">{detail}</div>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        className="inline-flex min-h-[44px] items-center rounded-xl border border-red-300 bg-white px-4 text-[13px] font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-45"
      >
        {button}
      </button>
    </div>
  );
}

/**
 * REMOVE FROM FLEET — confirmation. Same behaviour and words as the legacy
 * Fleet table's dialog (`pos/branding/FleetTable.tsx`): check-in is an
 * unauthenticated upsert, so a till still running the app comes back within a
 * minute, and the dialog says so rather than pretending removal is final.
 */
export function RemoveDeviceModal({
  row, busy, onClose, onConfirm,
}: { row: FleetRow; busy: boolean; onClose: () => void; onConfirm: () => void }) {
  const seenRecently = !!row.updatedAt && Date.now() - row.updatedAt < LIVE_WITHIN_MS;
  const label = row.registerName || row.deviceId || 'this device';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Remove from fleet">
      <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50">
            <Trash2 size={17} className="text-red-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-stone-900">Remove from fleet?</h3>
            <p className="mt-1 text-[12.5px] text-stone-600">
              <b className="text-stone-900">{label}</b>
              {row.locationName ? ` at ${row.locationName}` : ''} will be removed from the fleet list.
            </p>
          </div>
        </div>
        {seenRecently ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>This device checked in minutes ago.</b> Removing the record does not stop it —
              it will re-register on its next check-in and reappear here. Use this for a device
              that is genuinely gone.
            </div>
          </div>
        ) : (
          <p className="mt-3 rounded-lg bg-stone-50 p-2.5 text-[11.5px] text-stone-600">
            If this device is ever powered on with the POS app installed, it will re-register
            and reappear.
          </p>
        )}
        {row.pinnedVersion && (
          <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-[11.5px] text-rose-700">
            Its pin to <b className="font-mono">{row.pinnedVersion}</b> is discarded. If it comes
            back it follows the <b>{row.releaseRing}</b> ring instead.
          </p>
        )}
        <p className="mt-2 text-[11px] text-stone-400">
          The version history for this device is kept — an audit record outlives the row.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-[44px] rounded-xl px-4 text-[12.5px] font-bold text-stone-600 hover:bg-stone-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-4 text-[12.5px] font-bold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy && <RubyGemMark size={14} drawing color="currentColor" />}
            {busy ? 'Removing…' : 'Remove from fleet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DangerTab({ row, onRefresh }: DeviceTabProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const canManage = useCanManageFleet();
  const { commands, issue } = useDeviceCommands(row.deviceId, row.deviceType);
  const [pending, setPending] = useState<FleetActionId | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);

  const backToFleet = location.pathname.startsWith('/fleet-monitoring') ? '/fleet-monitoring' : '/pos-config?tab=fleet';
  const noDevice = !row.deviceId;
  const lockReason = !canManage ? 'You have view-only access to the fleet' : noDevice ? 'This row has never checked in' : undefined;

  const lastFailureFor = (action: FleetActionId): string | null =>
    commands.find((c: FleetCommand) => c.action === action && c.state === 'failed')?.failureReason ?? null;

  return (
    <div className="grid gap-4">
      <section className="rounded-2xl border border-red-300 bg-white">
        <div className="rounded-t-2xl border-b border-red-200 bg-[#FFF5F5] px-4 py-3.5">
          <h2 className="text-[15px] font-semibold text-red-700">Danger zone</h2>
          <p className="mt-1 text-[13px] text-stone-700">
            These stop the till from selling. Each one asks you to type the register name and enter a code we
            email you. If the till is offline, you can arm it to run on its next contact.
          </p>
        </div>
        <DangerRow
          title="Clear pairing"
          detail="Unpairs this register. The OS and data stay; someone must re-provision it at the store."
          button="Clear pairing…"
          disabled={!!lockReason}
          disabledReason={lockReason}
          onClick={() => setPending('clear_pairing')}
        />
        <DangerRow
          title="Factory wipe"
          detail="Erases everything on the till. Unsent sales on it are lost, so the confirm shows how many are waiting first."
          button="Factory wipe…"
          disabled={!!lockReason}
          disabledReason={lockReason}
          onClick={() => setPending('factory_wipe')}
        />
        <DangerRow
          title="Remove from fleet"
          detail="Deletes this device's record and any version pin. The till re-appears if it checks in again."
          button="Remove…"
          disabled={!!lockReason}
          disabledReason={lockReason}
          onClick={() => setRemoving(true)}
        />
        <DangerRow
          title="Reinstall OS"
          detail="Never possible remotely. The unit goes on the bench and is re-flashed over a cable from the Install tab."
          button="Reinstall…"
          disabled={!row.unitId || !canManage}
          disabledReason={!canManage ? 'You have view-only access to the fleet' : 'Only when the unit is on the bench — this device is not linked to a hardware unit'}
          onClick={() => navigate(`/pos-config?tab=install&unitId=${encodeURIComponent(row.unitId || '')}`)}
        />
      </section>

      {pending && (
        <DestructiveConfirm
          row={row}
          action={pending}
          busy={busy}
          lastFailureReason={lastFailureFor(pending)}
          onCancel={() => setPending(null)}
          onConfirm={async (body) => {
            setBusy(true);
            try {
              // Rethrows so DestructiveConfirm can read a `device_offline`
              // refusal and offer "Arm on next contact" itself.
              const created = await issue(pending, { reason: body.reason, codeId: body.codeId, code: body.code, params: body.params });
              toastService.success(
                created.state === 'queued' && !created.sentAt
                  ? `${pending.replace(/_/g, ' ')} armed · runs on next contact`
                  : `${pending.replace(/_/g, ' ')} sent`,
              );
              setPending(null);
              onRefresh();
            } catch (e: any) {
              // DestructiveConfirm shows the refusal inline; give it the
              // plain-language version, keeping `code` for the offline branch.
              if (e && e.code !== 'device_offline' && typeof e.message === 'string') e.message = humaniseCommandError(e.message);
              throw e;
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {removing && (
        <RemoveDeviceModal
          row={row}
          busy={removeBusy}
          onClose={() => setRemoving(false)}
          onConfirm={async () => {
            if (!row.deviceId) return;
            setRemoveBusy(true);
            try {
              await deviceConfigApi.deleteDevice(row.deviceType || DEFAULT_DEVICE_TYPE, row.deviceId);
              toastService.success(
                `Removed ${row.registerName || row.deviceId} from the fleet.`
                + (row.pinnedVersion ? ` Its pin to ${row.pinnedVersion} was discarded.` : ''),
              );
              setRemoving(false);
              navigate(backToFleet);
            } catch (e: any) {
              toastService.error(e?.message || 'Could not remove the device.');
            } finally {
              setRemoveBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
